import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import {
  Sun, Moon, User, Lock, Building2, Package, Bell, Shield,
  Eye, EyeOff, Save, Check, AlertCircle, LayoutDashboard
} from 'lucide-react';

export default function Settings() {
  const { user, theme, setTheme } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('info');
  const [saving, setSaving] = useState(false);

  const [dashPref, setDashPref] = useState(user?.dashboard_preference || '');
  const saveDashPref = async (val) => {
    setDashPref(val);
    try {
      await api('/api/auth/dashboard-preference', {
        method: 'PUT',
        body: JSON.stringify({ dashboard_preference: val || null })
      });
      flash('Dashboard preference saved. Reload to apply.', 'success');
    } catch { flash('Failed to save preference', 'error'); }
  };

  const [notifs, setNotifs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sp_notif_prefs') || '{}'); } catch { return {}; }
  });
  const toggleNotif = (key) => {
    const next = { ...notifs, [key]: !notifs[key] };
    setNotifs(next);
    localStorage.setItem('sp_notif_prefs', JSON.stringify(next));
  };

  // Role Permissions state
  const [permRole, setPermRole] = useState('staff');
  const [rolePerms, setRolePerms] = useState({});
  const [permDefs, setPermDefs] = useState([]);
  const [permLoading, setPermLoading] = useState(false);

  const loadPermissions = async (role) => {
    setPermLoading(true);
    try {
      const [allRes, defRes] = await Promise.all([
        api('/api/permissions/role/' + role),
        api('/api/permissions/definitions'),
      ]);
      setRolePerms(allRes.data || {});
      setPermDefs(defRes.data || []);
    } catch (e) { console.error('Failed to load permissions', e); }
    setPermLoading(false);
  };
  useEffect(() => { if (user?.role === 'admin') loadPermissions(permRole); }, [permRole]);

  const togglePerm = async (key) => {
    const newVal = !rolePerms[key];
    setRolePerms(prev => ({ ...prev, [key]: newVal }));
    try {
      await api('/api/permissions/role/' + permRole, {
        method: 'PUT',
        body: JSON.stringify({ permissions: { [key]: newVal } })
      });
      flash('Permission updated', 'success');
    } catch (e) { flash('Failed to update', 'error'); }
  };

  const flash = (text, type = 'info') => { setMsg(text); setMsgType(type); setTimeout(() => setMsg(''), 4000); };

  const updateProfile = async () => {
    setSaving(true);
    try {
      await api('/api/users/' + user.id, { method: 'PUT', body: JSON.stringify({ name, email }) });
      flash('Profile updated', 'success');
    } catch (err) { flash(err.error || 'Failed to update', 'error'); }
    setSaving(false);
  };

  const changePw = async () => {
    if (!newPw || newPw.length < 8) return flash('Password must be at least 8 characters', 'error');
    if (newPw !== confirmPw) return flash('Passwords do not match', 'error');
    setSaving(true);
    try {
      await api('/api/users/' + user.id, { method: 'PUT', body: JSON.stringify({ password: newPw }) });
      flash('Password changed', 'success');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) { flash(err.error || 'Failed', 'error'); }
    setSaving(false);
  };

  const isDark = theme === 'dark';
  const initials = (user?.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const pwValid = newPw.length >= 8;
  const pwMatch = confirmPw === newPw && confirmPw.length > 0;
  const pwReady = pwValid && pwMatch;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontFamily: 'Lora,serif', fontSize: 28, marginBottom: 8 }}>Settings</h1>
      <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 28 }}>Manage your account, preferences, and system configuration.</p>

      {msg && <Toast message={msg} type={msgType} />}

      {/* ═══ YOUR ACCOUNT ═══ */}
      <SectionLabel label="Your Account" />

      {/* Profile + Appearance side-by-side */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 20, marginBottom: 20 }}>
        <Card icon={User} title="Profile">
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 24, padding: '16px 18px', background: 'var(--navy)', borderRadius: 12, border: '1px solid var(--border)' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16, background: 'var(--gold)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Lora,serif', fontSize: 24, fontWeight: 700, color: 'var(--navy)', flexShrink: 0,
              boxShadow: '0 4px 12px rgba(201,168,76,.3)',
            }}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 2 }}>{user?.name}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>{user?.email}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <RoleBadge role={user?.role} />
                {user?.last_login && <span style={{ fontSize: 11, color: 'var(--faint)' }}>Last login {new Date(user.last_login).toLocaleDateString()}</span>}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group" style={{ margin: 0 }}><label>Name</label><input value={name} onChange={e => setName(e.target.value)} /></div>
            <div className="form-group" style={{ margin: 0 }}><label>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={updateProfile} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </Card>

        <Card icon={Sun} title="Appearance">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ThemePick active={isDark} onClick={() => setTheme('dark')} icon={Moon} label="Dark" desc="Navy & gold" colors={['#0D1B2A','#1A2F4A','#C9A84C','#fff']} />
            <ThemePick active={!isDark} onClick={() => setTheme('light')} icon={Sun} label="Light" desc="Clean & bright" colors={['#FFFFFF','#F4F6F9','#A68A3A','#1A1A2E']} />
          </div>
        </Card>
      </div>

      {/* Password */}
      <Card icon={Lock} title="Change Password" style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Current Password</label>
            <PwField value={currentPw} onChange={setCurrentPw} show={showCurrent} toggle={() => setShowCurrent(!showCurrent)} placeholder="Current" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>New Password</label>
            <PwField value={newPw} onChange={setNewPw} show={showNew} toggle={() => setShowNew(!showNew)} placeholder="Min 8 chars" />
            {newPw && <PwHint ok={pwValid} text={pwValid ? 'Length OK' : 'Too short'} />}
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Confirm</label>
            <PwField value={confirmPw} onChange={setConfirmPw} placeholder="Re-enter" />
            {confirmPw && <PwHint ok={pwMatch} text={pwMatch ? 'Match' : 'No match'} />}
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={changePw} disabled={!pwReady || saving}>
            <Lock size={14} /> Change Password
          </button>
        </div>
      </Card>

      {/* ═══ PREFERENCES ═══ */}
      <SectionLabel label="Preferences" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <Card icon={Bell} title="Notifications">
          {[
            { key: 'lowStock', label: 'Low stock alerts', desc: 'When items hit reorder point' },
            { key: 'poCreated', label: 'PO created', desc: 'New purchase order generated' },
            { key: 'poReceived', label: 'PO received', desc: 'Inventory received at location' },
            { key: 'dailySummary', label: 'Daily digest', desc: 'Morning inventory summary' },
            { key: 'weeklyReport', label: 'Weekly report', desc: 'End-of-week performance' },
          ].map(n => <Toggle key={n.key} {...n} checked={notifs[n.key]} onChange={() => toggleNotif(n.key)} />)}
          {/* TODO: Wire to PUT /api/settings/notifications when email delivery is ready */}
          <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 14, fontStyle: 'italic', paddingTop: 10, borderTop: '1px solid var(--border)' }}>Preferences saved locally. Email delivery coming soon.</div>
        </Card>

        <Card icon={LayoutDashboard} title="Default Dashboard">
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>Choose which page you see when you log in.</div>
          {[
            { value: '', label: 'Auto (based on role)', desc: 'Admin/Manager → Command Center, Staff → Operations' },
            { value: 'command-center', label: 'Command Center', desc: 'Full admin dashboard with KPIs and alerts' },
            { value: 'operations', label: 'Operations', desc: 'iPad-friendly daily operations page' },
          ].map(opt => (
            <button key={opt.value} onClick={() => saveDashPref(opt.value)} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', width: '100%',
              border: '2px solid ' + (dashPref === opt.value ? 'var(--gold)' : 'var(--border)'),
              borderRadius: 10, background: dashPref === opt.value ? 'var(--gold-pale)' : 'transparent',
              cursor: 'pointer', textAlign: 'left', transition: 'all .15s', marginBottom: 8, outline: 'none',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{opt.desc}</div>
              </div>
              {dashPref === opt.value && <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Check size={12} style={{ color: 'var(--navy)' }} />
              </div>}
            </button>
          ))}
        </Card>

        <Card icon={Shield} title="Security">
          <Toggle key="2fa" label="Two-factor authentication" desc="Extra security layer" checked={false} onChange={() => flash('2FA coming in a future update')} disabled />
          <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div><div style={{ fontSize: 14, fontWeight: 500 }}>Session timeout</div><div style={{ fontSize: 12, color: 'var(--muted)' }}>Auto-logout after inactivity</div></div>
            <span style={{ fontSize: 13, color: 'var(--faint)', background: 'var(--navy)', padding: '5px 14px', borderRadius: 8, border: '1px solid var(--border)' }}>24 hours</span>
          </div>
          <div style={{ padding: '14px 0' }}>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Active sessions</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--navy)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', boxShadow: '0 0 6px var(--success)' }} />
              <div><div style={{ fontSize: 13, fontWeight: 500 }}>Current session</div><div style={{ fontSize: 11, color: 'var(--faint)' }}>Active now</div></div>
            </div>
          </div>
          {/* TODO: Session listing/revocation API */}
        </Card>
      </div>

      {/* ═══ SYSTEM ═══ */}
      <SectionLabel label="System Configuration" admin={user?.role === 'admin'} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <Card icon={Building2} title="Organization" locked>
          {[
            { label: 'Company Name', value: 'StockPour Inc.' },
            { label: 'Currency', value: 'USD ($)' },
            { label: 'Timezone', value: 'America/New_York (ET)' },
            { label: 'Contact', value: user?.email || '-' },
          ].map((f, i) => <FieldRow key={i} {...f} />)}
          {/* TODO: org_settings table + GET/PUT /api/settings/org */}
        </Card>

        <Card icon={Package} title="Inventory Defaults" locked>
          {[
            { label: 'Reorder threshold', value: '25% of par max' },
            { label: 'Reorder quantity', value: '50% of par max' },
            { label: 'Low stock trigger', value: 'At or below par min' },
            { label: 'Unit types', value: 'case, bottle, keg, each' },
          ].map((f, i) => <FieldRow key={i} {...f} />)}
          {/* TODO: inventory_defaults table + GET/PUT /api/settings/inventory */}
        </Card>
      </div>

      {user?.role === 'admin' && (
        <>
          <SectionLabel label="Role Permissions" admin />
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 26px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--gold-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Shield size={17} style={{ color: 'var(--gold)' }} />
              </div>
              <h3 style={{ fontFamily: 'Lora,serif', fontSize: 17, margin: 0, fontWeight: 600 }}>Role Permissions</h3>
            </div>

            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              Configure what each role can see and do. Changes take effect on next login.
            </div>

            {/* Role tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
              {['admin', 'manager', 'staff', 'supplier'].map(r => (
                <button key={r} onClick={() => setPermRole(r)} style={{
                  padding: '8px 16px', borderRadius: 8, border: '1px solid ' + (permRole === r ? 'var(--gold)' : 'var(--border)'),
                  background: permRole === r ? 'var(--gold-pale)' : 'transparent',
                  color: permRole === r ? 'var(--gold)' : 'var(--muted)',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
                  fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
                }}>
                  {r}
                </button>
              ))}
            </div>

            {/* Permissions by category */}
            {permLoading ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)' }}>Loading...</div>
            ) : (
              ['Visibility', 'Purchasing', 'Products', 'Operations', 'Admin'].map(cat => {
                const catPerms = permDefs.filter(d => d.category === cat);
                if (!catPerms.length) return null;
                return (
                  <div key={cat} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>{cat}</div>
                    {catPerms.map(perm => (
                      <Toggle key={perm.key} label={perm.label} desc={perm.desc} checked={rolePerms[perm.key]} onChange={() => togglePerm(perm.key)} />
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      <style>{`
        @media (max-width:768px) {
          [style*="gridTemplateColumns: 3fr 2fr"],
          [style*="gridTemplateColumns:3fr 2fr"],
          [style*="gridTemplateColumns: 1fr 1fr"],
          [style*="gridTemplateColumns:1fr 1fr"],
          [style*="gridTemplateColumns: 1fr 1fr 1fr"],
          [style*="gridTemplateColumns:1fr 1fr 1fr"] { grid-template-columns:1fr !important; }
        }
      `}</style>
    </div>
  );
}

/* ── Sub-components ── */

function SectionLabel({ label, admin }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, marginTop: 8 }}>
      <h2 style={{ fontFamily: 'Lora,serif', fontSize: 15, color: 'var(--muted)', fontWeight: 600, margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</h2>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      {admin && <span style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 600, background: 'var(--gold-pale)', padding: '2px 8px', borderRadius: 8 }}>ADMIN</span>}
    </div>
  );
}

function Card({ icon: Icon, title, locked, children, style }) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 26px', position: 'relative', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--gold-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={17} style={{ color: 'var(--gold)' }} />
        </div>
        <h3 style={{ fontFamily: 'Lora,serif', fontSize: 17, margin: 0, fontWeight: 600 }}>{title}</h3>
        {locked && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: 'var(--faint)18', color: 'var(--faint)', letterSpacing: '.3px', border: '1px solid var(--border)' }}>COMING SOON</span>}
      </div>
      <div style={{ opacity: locked ? .55 : 1, pointerEvents: locked ? 'none' : 'auto' }}>{children}</div>
    </div>
  );
}

function ThemePick({ active, onClick, icon: Icon, label, desc, colors }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
      border: '2px solid ' + (active ? 'var(--gold)' : 'var(--border)'),
      borderRadius: 12, background: active ? 'var(--gold-pale)' : 'transparent',
      cursor: 'pointer', textAlign: 'left', transition: 'all .15s', width: '100%', outline: 'none',
    }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = 'var(--gold)50'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {/* mini preview */}
      <div style={{ width: 48, height: 36, borderRadius: 6, overflow: 'hidden', display: 'flex', border: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ width: 12, background: colors[1] }} />
        <div style={{ flex: 1, background: colors[0], padding: 4 }}>
          <div style={{ height: 3, width: '60%', background: colors[2], borderRadius: 1, marginBottom: 3 }} />
          <div style={{ height: 2, width: '80%', background: colors[3] + '30', borderRadius: 1, marginBottom: 2 }} />
          <div style={{ height: 2, width: '40%', background: colors[3] + '20', borderRadius: 1 }} />
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{desc}</div>
      </div>
      {active && <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Check size={12} style={{ color: 'var(--navy)' }} />
      </div>}
    </button>
  );
}

function Toggle({ label, desc, checked, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0', borderBottom: '1px solid var(--border)', opacity: disabled ? .45 : 1 }}>
      <div><div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>{desc && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>{desc}</div>}</div>
      <label style={{ position: 'relative', width: 44, height: 24, cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
        <input type="checkbox" checked={!!checked} onChange={disabled ? undefined : onChange} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
        <span style={{ position: 'absolute', inset: 0, borderRadius: 12, transition: 'background .2s', background: checked ? 'var(--gold)' : 'var(--faint)40' }} />
        <span style={{ position: 'absolute', top: 3, left: checked ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
      </label>
    </div>
  );
}

function FieldRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', opacity: .6 }}>{value}</span>
    </div>
  );
}

function PwField({ value, onChange, show, toggle, placeholder }) {
  return (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ paddingRight: toggle ? 38 : undefined }} />
      {toggle && <button type="button" onClick={toggle} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faint)', padding: 2 }}>{show ? <EyeOff size={15} /> : <Eye size={15} />}</button>}
    </div>
  );
}

function PwHint({ ok, text }) {
  return <div style={{ fontSize: 11, color: ok ? 'var(--success)' : 'var(--danger)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>{ok ? <Check size={11} /> : <AlertCircle size={11} />} {text}</div>;
}

function Toast({ message, type }) {
  const c = type === 'success' ? 'var(--success)' : type === 'error' ? 'var(--danger)' : 'var(--gold)';
  return (
    <div style={{ padding: '12px 18px', borderRadius: 12, marginBottom: 20, fontSize: 14, fontWeight: 500, background: c + '12', color: c, border: '1px solid ' + c + '30', display: 'flex', alignItems: 'center', gap: 8 }}>
      {type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />} {message}
    </div>
  );
}

function RoleBadge({ role }) {
  const m = { admin: { bg: 'var(--gold-pale)', c: 'var(--gold)' }, manager: { bg: 'rgba(74,158,191,.12)', c: '#4A9EBF' }, staff: { bg: 'rgba(91,196,192,.12)', c: '#5BC4C0' }, supplier: { bg: 'rgba(176,127,212,.12)', c: '#B07FD4' } };
  const s = m[role] || m.staff;
  return <span style={{ padding: '3px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: s.bg, color: s.c, textTransform: 'capitalize', border: '1px solid ' + s.c + '20' }}>{role}</span>;
}
